import { Link } from "@tanstack/react-router";
import { ArrowRight, MessageSquare, ArrowBigUp, HelpCircle } from "lucide-react";

export interface ForumTeaserPost {
  slug: string;
  title: string;
  post_type?: string | null;
  comment_count?: number | null;
  vote_score?: number | null;
  category?: { slug: string; name: string } | null;
}

/**
 * Kompakt "senast i forumet"-lista. Renderas server-side från startsidans loader
 * så både besökare och crawlers ser färsk community-aktivitet direkt.
 */
export function ForumTeaser({ posts }: { posts: ForumTeaserPost[] }) {
  if (!posts.length) return null;

  return (
    <section className="container-edge py-20 md:py-28">
      <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="eyebrow text-muted-foreground">Fritidsforum</p>
            <h2 className="mt-2 font-display text-3xl leading-tight md:text-4xl">
              Senast i <span className="font-italic-display headline-accent">communityt</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
              Sveriges community för husbil och husvagn — ställ frågor, dela resor och få svar
              från andra ägare och från oss i verkstaden.
            </p>
          </div>
          <Link
            to="/forum"
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground transition hover:opacity-95"
          >
            Öppna forumet <ArrowRight size={16} />
          </Link>
        </div>

        <ul className="mt-8 divide-y divide-border/70 border-t border-border/70">
          {posts.slice(0, 6).map((p) => (
            <li key={p.slug}>
              <Link
                to="/forum/t/$slug"
                params={{ slug: p.slug }}
                className="flex items-start gap-3 py-4 transition hover:bg-secondary/50 md:items-center md:gap-4 md:px-2"
              >
                <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/10 text-accent md:mt-0">
                  {p.post_type === "question" ? <HelpCircle size={16} /> : <MessageSquare size={16} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold md:text-base">{p.title}</span>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {p.category?.name ? `${p.category.name} · ` : ""}
                    {p.comment_count ?? 0} svar
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-muted-foreground">
                  <ArrowBigUp size={15} /> {Math.max(0, p.vote_score ?? 0)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
